import React, { ReactNode, useEffect, useState } from 'react'
import { Provider } from 'react-redux'
import Cookies from 'universal-cookie'
import { store } from './redux/store'

const cookies = new Cookies()

interface ReferralResponse {
  referralValid: boolean
  couponCode?: string
  banner?: string
}

interface ReferralContextValue {
  state: string[]
  updateReferralState: (referrals: string[]) => void
}

const ReferralStateContext = React.createContext<
  ReferralContextValue | undefined
>(undefined)

async function referralValidWithResponse(
  referral: string
): Promise<ReferralResponse | false> {
  if (!referral) {
    return false
  }

  try {
    let result: ReferralResponse = await fetch(
      `https://jp5vfaodlc.execute-api.us-east-2.amazonaws.com/CheckSalesforceReports?var=${referral}`
    ).then((res) => res.json())

    if (!result.referralValid) {
      const referralArray = referral.split('-')
      if (referralArray.length > 2) {
        referral = `${referralArray[0]}-${referralArray[1]}`
        result = await fetch(
          `https://jp5vfaodlc.execute-api.us-east-2.amazonaws.com/CheckSalesforceReports?var=${referral}`
        ).then((res) => res.json())
      }
    }

    return result
  } catch {
    return false
  }
}

export const hideCouponHeader = (ref?: string[]): boolean => {
  if (!ref) {
    return false
  }

  return ref.some((r) => r.toLowerCase().includes('/n'))
}

export const getCouponCodes = (): string[] => {
  const coupons = cookies.get('coupon')
  return Array.isArray(coupons) ? coupons : ['COOLPERSONCREDIT']
}

export const bannerMessage = (): string => {
  const value = cookies.get('bannerMessage')
  return typeof value === 'string' ? value : ''
}

interface ReferralProviderProps {
  children: ReactNode
}

export const ReferralProvider: React.FunctionComponent<
  ReferralProviderProps
> = ({ children }) => {
  const [state, setState] = useState<string[]>([])

  useEffect(() => {
    let referrals: string[] = []
    const referralCookieValue = cookies.get('referral')
    const referralCookies = Array.isArray(referralCookieValue)
      ? referralCookieValue
      : []
    let banner = ''

    try {
      referrals = window.location.hash
        .split('#')[1]
        .split('/')[0]
        .split('&')
        .filter((value) => value !== '')

      if (referrals.length === 0) {
        referrals = referralCookies
      }
    } catch {
      referrals = referralCookies
    }

    const fetchData = async () => {
      const validReferrals: string[] = []
      const couponCodes: string[] = []
      const invalidReferrals: string[] = []

      for (const referral of referrals) {
        const response = await referralValidWithResponse(referral)

        if (response && response.referralValid) {
          validReferrals.push(referral)

          if (typeof response.couponCode === 'string') {
            couponCodes.push(response.couponCode)
          }

          if (!banner && typeof response.banner === 'string') {
            banner = response.banner
          }
        } else {
          invalidReferrals.push(referral)
        }
      }

      const expires = new Date()
      expires.setTime(expires.getTime() + 2 * 60 * 60 * 1000)

      if (validReferrals.length > 0) {
        cookies.set('referral', validReferrals, { expires })
        cookies.set('bannerMessage', banner, { expires })
        cookies.set('coupon', couponCodes, { expires })
        setState(validReferrals)
      }
    }

    fetchData()
  }, [])

  const updateReferralState = (referrals: string[]) => {
    setReferralState(referrals).then((result) => {
      setState(result)
    })
  }

  return (
    <ReferralStateContext.Provider value={{ state, updateReferralState }}>
      {children}
    </ReferralStateContext.Provider>
  )
}

const Wrapper = ({ element }: { element: ReactNode }) => {
  return (
    <Provider store={store}>
      <ReferralProvider>{element}</ReferralProvider>
    </Provider>
  )
}

export default Wrapper

function useReferralContext(): ReferralContextValue {
  const context = React.useContext(ReferralStateContext)

  if (!context) {
    throw new Error('useReferralContext must be used within ReferralProvider')
  }

  return context
}

export function useReferralState(): string[] {
  return useReferralContext().state
}

export function useUpdateReferralState(): (
  referrals: string[]
) => void {
  return useReferralContext().updateReferralState
}

export function getReferralLink(): string[] {
  const referral = cookies.get('referral')
  return Array.isArray(referral) ? referral : []
}

export async function setReferralState(
  referrals: string[]
): Promise<string[]> {
  if (referrals.length > 0) {
    const existingReferral = cookies.get('referral')
    referrals = Array.isArray(existingReferral) ? existingReferral : referrals

    const validReferrals: string[] = []
    const couponCodes: string[] = []
    const invalidReferrals: string[] = []

    for (const referral of referrals) {
      const response = await referralValidWithResponse(referral)

      if (response && response.referralValid) {
        validReferrals.push(referral)

        if (typeof response.couponCode === 'string') {
          couponCodes.push(response.couponCode)
        }
      } else {
        invalidReferrals.push(referral)
      }
    }

    const expires = new Date()
    expires.setTime(expires.getTime() + 2 * 60 * 60 * 1000)

    if (validReferrals.length > 0) {
      cookies.set('referral', validReferrals, { expires })
      cookies.set('coupon', couponCodes, { expires })
      return validReferrals
    }

    cookies.set('referral', [], { expires })
  }

  return []
}
